/**
 * Industry HTTP client.
 *
 * Backend exposes:
 *   GET    /industries                            — catalog
 *   GET    /industries/business/:businessId       — current type for a business
 *   PATCH  /industries/business/:businessId       — change it (body: { industryType })
 *
 * The rich UI config lives in @/constants/industries.ts. The backend
 * only stores the type ID.
 */
import api from "@/services/api";
import type { IndustryId } from "@/constants/industries";

export interface IndustryDescriptor {
  id: string;
  label: string;
  description: string;
  isDefault?: boolean;
}

export interface IndustryCatalog {
  industries: IndustryDescriptor[];
  default: string;
}

export interface BusinessIndustry {
  businessId: string;
  businessName: string;
  businessSlug: string;
  industryType: IndustryId;
  descriptor: IndustryDescriptor | null;
}

interface UnwrappedResponse<T> {
  success: boolean;
  data: T;
}

function unwrap<T>(body: UnwrappedResponse<T> | T): T {
  if (
    body &&
    typeof body === "object" &&
    "data" in (body as object) &&
    "success" in (body as object)
  ) {
    return (body as UnwrappedResponse<T>).data;
  }
  return body as T;
}

export const industryService = {
  async listIndustries(): Promise<IndustryCatalog> {
    const res =
      await api.get<UnwrappedResponse<IndustryCatalog>>("/industries");
    return unwrap(res.data);
  },

  async getForBusiness(businessId: string): Promise<BusinessIndustry> {
    const res = await api.get<UnwrappedResponse<BusinessIndustry>>(
      `/industries/business/${businessId}`,
    );
    return unwrap(res.data);
  },

  async setForBusiness(
    businessId: string,
    industryType: IndustryId,
  ): Promise<BusinessIndustry> {
    const res = await api.patch<UnwrappedResponse<BusinessIndustry>>(
      `/industries/business/${businessId}`,
      { industryType },
    );
    return unwrap(res.data);
  },
};

export default industryService;
